import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { switchMap, map, catchError, of, concatMap } from "rxjs";
import { CountryClient, CountryProjectManagementClient } from "src/api-service";
import { normalizeError } from "../../../../app-store-error.types";
import { CountriesActions } from "./countries.actions";

@Injectable()
export class CountriesEffects {
    private readonly actions$ = inject(Actions);
    private readonly countryClient = inject(CountryClient);
    private readonly countryProjectManagementClient = inject(CountryProjectManagementClient);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CountriesActions.load),
            switchMap(() =>
                this.countryClient.getAll().pipe(
                    map(countries =>
                        CountriesActions.loadSuccess({ countries }),
                    ),
                    catchError(error =>
                        of(
                            CountriesActions.loadFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.COUNTRIES.PROJECT_MANAGEMENT.TOAST.LOAD_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    createCountry$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CountriesActions.createCountry),
            concatMap(({ request }) =>
                this.countryClient.create(request).pipe(
                    concatMap(() => [
                        CountriesActions.createCountrySuccess({
                            toastKey: "ADMIN.SUPER_USER.COUNTRIES.TOAST.CREATE_SUCCESS",
                        }),
                        CountriesActions.load(),
                    ]),
                    catchError(error =>
                        of(
                            CountriesActions.createCountryFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.COUNTRIES.TOAST.CREATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    createCountryProjectManagement$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CountriesActions.createCountryProjectManagement),
            concatMap(({ request }) =>
                this.countryProjectManagementClient.create(request).pipe(
                    concatMap(() => [
                        CountriesActions.createCountryProjectManagementSuccess({
                            toastKey: "ADMIN.SUPER_USER.COUNTRIES.PROJECT_MANAGEMENT.TOAST.CREATE_SUCCESS",
                        }),
                        CountriesActions.load(),
                    ]),
                    catchError(error =>
                        of(
                            CountriesActions.createCountryProjectManagementFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.COUNTRIES.PROJECT_MANAGEMENT.TOAST.CREATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    updateCountryProjectManagement$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CountriesActions.updateCountryProjectManagement),
            concatMap(({ request, id }) =>
                this.countryProjectManagementClient.update(id, request).pipe(
                    concatMap(() => [
                        CountriesActions.updateCountryProjectManagementSuccess({
                            toastKey: "ADMIN.SUPER_USER.COUNTRIES.PROJECT_MANAGEMENT.TOAST.UPDATE_SUCCESS",
                        }),
                        CountriesActions.load(),
                    ]),
                    catchError(error =>
                        of(
                            CountriesActions.updateCountryProjectManagementFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.COUNTRIES.PROJECT_MANAGEMENT.TOAST.UPDATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteCountryProjectManagement$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CountriesActions.deleteCountryProjectManagement),
            concatMap(({ id }) =>
                this.countryProjectManagementClient.delete(id).pipe(
                    concatMap(() => [
                        CountriesActions.deleteCountryProjectManagementSuccess({
                            toastKey: "ADMIN.SUPER_USER.COUNTRIES.PROJECT_MANAGEMENT.TOAST.DELETE_SUCCESS",
                        }),
                        CountriesActions.load(),
                    ]),
                    catchError(error =>
                        of(
                            CountriesActions.deleteCountryProjectManagementFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.COUNTRIES.PROJECT_MANAGEMENT.TOAST.DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}
